import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Paper,
  useTheme,
  Button,
  IconButton
} from "@mui/material";
import {
  FolderOpen as TotalIcon,
  CheckCircle as ClosedIcon,
  HourglassBottom as OpenIcon,
  Send as ForwardedIcon,
  Today as TodayIcon,
  Event as TomorrowIcon,
  People as UsersIcon,
  Refresh as RefreshIcon,
  WalletOutlined,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { hosturl } from "../libs/Constant";
import { useNavigate } from "react-router-dom";

const AdminFormMatrix = () => {
  const [matrixData, setMatrixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [role, setRole] = useState(null);
  const theme = useTheme();
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const fetchFormMatrix = async () => {
    try {
      const endpoint = role === "user" ? "/user-from-matrix" : "/admin-from-matrix";
      const response = await fetch(`${hosturl}${endpoint}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to fetch form matrix");
      const data = await response.json();
      setMatrixData(data.result);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");

    if (storedToken && storedRole) {
      setRole(storedRole);
    } else {
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (role) {
      fetchFormMatrix();
      const interval = setInterval(fetchFormMatrix, 30000);
      return () => clearInterval(interval);
    }
  }, [role]);

  const cardColors = {
    primary: {
      main: '#3f51b5',
      light: '#e8eaf6',
      dark: '#303f9f'
    },
    success: {
      main: '#4caf50',
      light: '#e8f5e9',
      dark: '#388e3c'
    },
    warning: {
      main: '#ff9800',
      light: '#fff3e0',
      dark: '#f57c00'
    },
    info: {
      main: '#2196f3',
      light: '#e3f2fd',
      dark: '#1976d2'
    },
    secondary: {
      main: '#9c27b0',
      light: '#f3e5f5',
      dark: '#7b1fa2'
    },
    neutral: {
      main: '#607d8b',
      light: '#eceff1',
      dark: '#455a64'
    }
  };

  const StatCard = ({ icon, title, value, color }) => (
    <motion.div whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
      <Card
        sx={{
          height: '100%',
          borderRadius: '12px',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.08)',
          background: 'white',
          borderLeft: `4px solid ${cardColors[color].main}`,
          '&:hover': {
            boxShadow: '0 8px 24px 0 rgba(0,0,0,0.12)'
          }
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Avatar
              sx={{
                bgcolor: cardColors[color].light,
                mr: 2,
                color: cardColors[color].main,
                width: 48,
                height: 48
              }}
            >
              {icon}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: cardColors[color].dark }}>
                {value}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress size={60} thickness={4} sx={{ color: cardColors.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '12px', background: 'white' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchFormMatrix}
        >
          Refresh Data
        </Button>
      </Paper>
    );
  }

  const adminCards = [
    {
      icon: <TotalIcon />,
      title: "Total Currents",
      value: matrixData?.totalCurrent,
      color: "primary",
    },
    {
      icon: <ClosedIcon />,
      title: "Closed Currents",
      value: matrixData?.closed,
      color: "success",
    },
    {
      icon: <OpenIcon />,
      title: "Open Currents",
      value: matrixData?.open,
      color: "warning",
    },
    {
      icon: <ForwardedIcon />,
      title: "Forwarded",
      value: matrixData?.forwarded,
      color: "info",
    },
    {
      icon: <TodayIcon />,
      title: "Today's Hearings",
      value: matrixData?.today,
      color: "secondary",
    },
    {
      icon: <TomorrowIcon />,
      title: "Tomorrow's Hearings",
      value: matrixData?.tomorrow,
      color: "neutral",
    },
    {
      icon: <UsersIcon />,
      title: "Total Staff upto Now",
      value: matrixData?.users,
      color: "primary",
    },
    {
      icon: <WalletOutlined />,
      title: "Amount Recovered",
      value: matrixData?.totalAmount,
      color: "success",
      
    },
  ];

  const userCards = [
    {
      icon: <TotalIcon />,
      title: "Total Currents Upto Now",
      value: matrixData?.totalCurrent,
      color: "primary",
    },
    {
      icon: <ClosedIcon />,
      title: "Completed Currents",
      value: matrixData?.completed,
      color: "success",
    },
    {
      icon: <TodayIcon />,
      title: "Today's Hearings",
      value: matrixData?.today,
      color: "secondary",
    },
    {
      icon: <TomorrowIcon />,
      title: "Tomorrow's Hearings",
      value: matrixData?.tomorrow,
      color: "neutral",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Dashboard Overview
        </Typography>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" sx={{ mr: 1 }}>
            Last updated:
          </Typography>
          <Typography variant="body2" fontWeight={600} color="primary">
            {lastUpdated.toLocaleTimeString()}
          </Typography>
          <IconButton size="small" onClick={fetchFormMatrix}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {(role === "admin" ? adminCards : userCards).map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      {role === "admin" && (
        <Box mt={5}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Section-wise Overview
          </Typography>
          {matrixData.sections &&
            Object.entries(matrixData.sections).map(([sectionName, sectionData], idx) => (
              <Box key={sectionName} mb={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Section: {sectionName.trim()}
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      icon={<TotalIcon />}
                      title="Total"
                      value={sectionData.totalCurrent}
                      color="primary"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      icon={<ClosedIcon />}
                      title="Closed"
                      value={sectionData.closed}
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      icon={<OpenIcon />}
                      title="Open"
                      value={sectionData.open}
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      icon={<ForwardedIcon />}
                      title="Forwarded"
                      value={sectionData.forwarded}
                      color="info"
                    />
                  </Grid>
                 <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      icon={<TodayIcon />}
                      title="Today's Hearings"
                      value={sectionData.today}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3.3}>
                    <StatCard
                      icon={<TomorrowIcon />}
                      title="Tomorrows Hearings"
                      value={sectionData.tomorrow}
                      color="info"
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default AdminFormMatrix;
